import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Μπορώ να κάνω τατουάζ το καλοκαίρι;", acceptedAnswer: { "@type": "Answer", text: "Ναι, αλλά χρειάζεται προσοχή. Το καλοκαίρι δεν αποκλείει τατουάζ, αποκλείει αμέλεια στη φροντίδα. Αν αποφύγεις ήλιο, θάλασσα και πισίνα για τον πρώτο μήνα, η επούλωση πάει κανονικά." } },
    { "@type": "Question", name: "Πόσο καιρό να μην πάω θάλασσα μετά το τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Τουλάχιστον 4 εβδομάδες. Το αλάτι, τα βακτήρια και το νερό επηρεάζουν σοβαρά ένα τατουάζ που επουλώνεται." } },
    { "@type": "Question", name: "Τι SPF να χρησιμοποιήσω στο τατουάζ μετά την επούλωση;", acceptedAnswer: { "@type": "Answer", text: "SPF 50 ή και ψηλότερο. Ο ήλιος είναι ο κύριος λόγος που τα τατουάζ ξεθωριάζουν πρόωρα." } },
  ],
};

export default function SummerTattooCareAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Τατουάζ το καλοκαίρι: ήλιος, θάλασσα και σωστή φροντίδα",
      "description": "Όλα όσα πρέπει να ξέρεις για tattoo το καλοκαίρι: ήλιος, θάλασσα, ιδρώτας και πώς να φροντίσεις σωστά ένα φρέσκο τατουάζ κατά τους καλοκαιρινούς μήνες.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-04-25",
      "dateModified": "2026-04-25",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/summer-tattoo-care-athens",
      "inLanguage": "el"
    };
    inject('schema-summer-care-faq', faqSchema);
    inject('schema-summer-care-article', articleSchema);
    return () => {
      document.getElementById('schema-summer-care-faq')?.remove();
      document.getElementById('schema-summer-care-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Τατουάζ το Καλοκαίρι: Ήλιος, Θάλασσα και Φροντίδα | 210 Tattoo Athens</title>
        <meta name="description" content="Όλα όσα πρέπει να ξέρεις για tattoo το καλοκαίρι: ήλιος, θάλασσα, ιδρώτας και σωστή φροντίδα φρέσκου τατουάζ." />
        <link rel="canonical" href="https://210tattoo.gr/blog/summer-tattoo-care-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/summer-tattoo-care-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/summer-tattoo-care-athens" />
        <meta property="og:title" content="Τατουάζ το Καλοκαίρι: Ήλιος, Θάλασσα και Φροντίδα | 210 Tattoo Athens" />
        <meta property="og:description" content="Καλοκαίρι και τατουάζ: ήλιος, θάλασσα, ιδρώτας και σωστή φροντίδα." />
        <meta property="og:url" content="https://210tattoo.gr/blog/summer-tattoo-care-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('summer-tattoo-care-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Τατουάζ το Καλοκαίρι | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Ήλιος, θάλασσα και φρέσκο tattoo: τι να προσέχεις το καλοκαίρι." />
        <meta name="twitter:image" content={ogImage('summer-tattoo-care-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Φροντίδα Τατουάζ Καλοκαίρι</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Όσα πρέπει να ξέρεις</span>
              <br />
              <span className="text-white">για τατουάζ το καλοκαίρι στην Αθήνα</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">25 Απριλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Το καλοκαίρι δεν αποκλείει τατουάζ. Αποκλείει αμέλεια. Αν ξέρεις τι να αποφύγεις και πώς να φροντίσεις σωστά, μπορείς να κάνεις τατουάζ ακόμα και τον Αύγουστο.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Can you tattoo in summer? */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Μπορείς να κάνεις <span className="text-pink-500">τατουάζ το καλοκαίρι;</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Η σύντομη απάντηση: ναι. Η πλήρης απάντηση: ναι, αλλά με προϋποθέσεις που το χειμώνα δεν υπάρχουν.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Οι ψηλές θερμοκρασίες, ο ήλιος, η θάλασσα και ο ιδρώτας δημιουργούν ένα περιβάλλον που κάνει την επούλωση πιο απαιτητική. Δεν είναι αδύνατο να επουλωθεί καλά ένα καλοκαιρινό tattoo, αλλά απαιτεί πιο προσεκτική φροντίδα.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Ο ήλιος είναι το μεγαλύτερο πρόβλημα', desc: 'UV ακτινοβολία σε φρέσκο tattoo μπορεί να προκαλέσει ξεθώριασμα, φαγούρα και παραμόρφωση του σχεδίου κατά την επούλωση.' },
                { title: 'Η θάλασσα και τα μικρόβια', desc: 'Θαλασσινό νερό και πισίνες φέρουν βακτήρια που σε ένα ανοιχτό δέρμα που επουλώνεται μπορεί να οδηγήσουν σε μόλυνση.' },
                { title: 'Ο ιδρώτας επηρεάζει την επούλωση', desc: 'Υπερβολικός ιδρώτας κρατά υγρό το δέρμα και δυσκολεύει τη φυσιολογική διαδικασία επούλωσης.' },
                { title: 'Αλλά είναι διαχειρίσιμο', desc: 'Με σωστή φροντίδα, αποφυγή ήλιου και θάλασσας για τις πρώτες εβδομάδες, το αποτέλεσμα είναι εξίσου καλό.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Sun protection */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Αντηλιακό και <span className="text-pink-500">προστασία από τον ήλιο</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Ο ήλιος είναι ο χειρότερος εχθρός ενός tattoo, φρέσκου ή παλιού. Για φρέσκο tattoo ισχύει απόλυτη αποφυγή για τουλάχιστον 3–4 εβδομάδες.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Μετά την επούλωση, η χρήση αντηλιακού SPF 50 είναι η πιο αποτελεσματική συνήθεια για να διατηρήσεις τα χρώματα και τις λεπτομέρειες για χρόνια.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Πρώτες 3–4 εβδομάδες: αποφυγή ήλιου', desc: 'Κάλυψε το τατουάζ με ρούχο όταν βγαίνεις έξω. Μην εφαρμόσεις αντηλιακό σε φρέσκο tattoo, το δέρμα δεν έχει κλείσει ακόμα.' },
                { title: 'Μετά την επούλωση: SPF 50 πάντα', desc: 'Κάθε φορά που εκτίθεσαι στον ήλιο, βάλε αντηλιακό στο τατουάζ. Αυτό επιβραδύνει σημαντικά το ξεθώριασμα.' },
                { title: 'Τα fine line tattoos είναι πιο ευαίσθητα', desc: 'Λεπτές γραμμές και μικρές λεπτομέρειες ξεθωριάζουν γρηγορότερα από exposure στον ήλιο. Η προστασία είναι ακόμα πιο σημαντική.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Sea/pool and healing */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θάλασσα, πισίνα <span className="text-pink-500">και επούλωση</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το νερό, θαλασσινό ή πισίνας, είναι απαγορευτικό για φρέσκο tattoo. Ο λόγος δεν είναι μόνο υγιεινής αλλά και ποιότητας αποτελέσματος.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              {[
                { title: 'Θάλασσα', desc: 'Αλάτι + βακτήρια + UV = ό,τι χειρότερο για φρέσκο tattoo. Αποφυγή για τουλάχιστον 4 εβδομάδες.' },
                { title: 'Πισίνα', desc: 'Το χλώριο είναι εξίσου επιβλαβές. Ερεθίζει το δέρμα και παρεμβαίνει στη διαδικασία επούλωσης.' },
                { title: 'Μπάνιο σπιτιού', desc: 'Μικρά, γρήγορα ντους είναι εντάξει. Αποφυγή μπανιέρας και παρατεταμένης έκθεσης σε νερό.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Ακόμα και αν το τατουάζ φαίνεται επουλωμένο εξωτερικά σε 2 εβδομάδες, το δέρμα συνεχίζει να αναγεννάται σε βαθύτερα στρώματα για 4–6 εβδομάδες ακόμα.
            </p>
          </div>
        </section>

        {/* Sweat and summer care */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Ιδρώτας και <span className="text-pink-500">καλοκαιρινή φροντίδα</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Ο ιδρώτας δεν είναι ουδέτερος για ένα φρέσκο tattoo. Κρατά το δέρμα υγρό, δημιουργεί συνθήκες για βακτηριακή ανάπτυξη και μπορεί να προκαλέσει φαγούρα που οδηγεί σε ξύσιμο.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Ελαφρά, αναπνεύσιμα ρούχα', desc: 'Αν το τατουάζ καλύπτεται από ρούχα, επέλεξε λεπτά βαμβακερά που δεν τρίβουν και αφήνουν το δέρμα να αναπνέει.' },
                { title: 'Συχνό καθάρισμα', desc: 'Το καλοκαίρι ίσως χρειαστεί να πλένεις το τατουάζ πιο συχνά, απαλά, με ουδέτερο σαπούνι, και να το στεγνώνεις καλά.' },
                { title: 'Αποφυγή έντονης γυμναστικής', desc: 'Τις πρώτες 2 εβδομάδες, γυμναστική που προκαλεί έντονο ιδρώτα στην περιοχή του tattoo δεν συνιστάται.' },
                { title: 'Ενυδάτωση χωρίς υπερβολή', desc: 'Βάζε ενυδατική κρέμα σε λεπτή στρώση, το δέρμα χρειάζεται υγρασία αλλά όχι ασφυξία, ειδικά το καλοκαίρι.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Better to wait for autumn? */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Καλύτερα να <span className="text-pink-500">περιμένεις το φθινόπωρο;</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Αν έχεις πλάνα για παραλία τις επόμενες 4–6 εβδομάδες, ίσως αξίζει να σκεφτείς να αναβάλεις. Αλλά αν μπορείς να διαχειριστείς τους περιορισμούς, το καλοκαίρι δεν είναι απαγορευτικό.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Φθινόπωρο/χειμώνας = πιο εύκολη επούλωση', desc: 'Λιγότερος ήλιος, λιγότερος ιδρώτας, λιγότεροι περιορισμοί. Αν δεν έχεις βιασύνη, είναι η καλύτερη εποχή.' },
                { title: 'Καλοκαίρι = εντάξει αν σχεδιαστεί σωστά', desc: 'Αν κάνεις το τατουάζ νωρίς καλοκαίρι και έχεις χρόνο επούλωσης πριν τις διακοπές, δεν υπάρχει πρόβλημα.' },
                { title: 'Ο σχεδιασμός κάνει τη διαφορά', desc: 'Κλείσε ραντεβού με βάση το ταξιδιωτικό σου πρόγραμμα. Ένας καλός artist θα σε βοηθήσει να βρεις τον σωστό χρόνο.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Summer aftercare mistakes */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Συχνά λάθη στη <span className="text-pink-500">καλοκαιρινή φροντίδα</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Τα περισσότερα προβλήματα με καλοκαιρινά tattoos δεν οφείλονται στην εποχή, οφείλονται σε αποφάσεις που έγιναν κατά τη διάρκεια της επούλωσης.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Βουτιά στη θάλασσα «γιατί φαίνεται καλά»', desc: 'Εξωτερική επούλωση δεν σημαίνει ολοκληρωμένη επούλωση. Το δέρμα συνεχίζει να επουλώνεται εσωτερικά για εβδομάδες.' },
                { title: 'Αντηλιακό σε φρέσκο tattoo', desc: 'Το αντηλιακό δεν εφαρμόζεται σε ανοιχτό δέρμα. Αντί γι\' αυτό, κάλυψε το τατουάζ με ρούχο.' },
                { title: 'Ξύσιμο λόγω φαγούρας', desc: 'Η φαγούρα είναι φυσιολογική κατά την επούλωση. Το ξύσιμο όμως μπορεί να αφαιρέσει χρώμα και να δημιουργήσει ουλές.' },
                { title: 'Λιπαρές κρέμες σε υπερβολή', desc: 'Η υπερβολική ενυδάτωση, ειδικά το καλοκαίρι, δεν αφήνει το δέρμα να αναπνέει και μπορεί να επιμηκύνει την επούλωση.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 210 approach in summer */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς δουλεύουμε <span className="text-pink-500">το καλοκαίρι στο 210 Tattoo Athens</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Πριν κάθε συνεδρία το καλοκαίρι, κάνουμε μια σύντομη συζήτηση για το τι έχεις πρόγραμμα τις επόμενες εβδομάδες, για να σχεδιάσουμε σωστά το timing και να σου δώσουμε αναλυτικές οδηγίες φροντίδας για την εποχή.
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Timing consultation', desc: 'Αν έχεις διακοπές στο άμεσο μέλλον, το συζητάμε και βρίσκουμε τη σωστή ημερομηνία για το τατουάζ.' },
                { step: '02', title: 'Placement με βάση τον τρόπο ζωής', desc: 'Σημεία που συνήθως εκτίθενται στον ήλιο ή καλύπτονται άσχημα το καλοκαίρι λαμβάνονται υπόψη.' },
                { step: '03', title: 'Αναλυτικές καλοκαιρινές οδηγίες', desc: 'Μετά τη συνεδρία παίρνεις οδηγίες φροντίδας προσαρμοσμένες στην εποχή, όχι γενικές.' },
                { step: '04', title: 'Follow-up support', desc: 'Είμαστε διαθέσιμοι για ερωτήσεις κατά την επούλωση, αν κάτι φαίνεται διαφορετικό από αυτό που περίμενες.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θέλεις να κάνεις τατουάζ <span className="text-pink-500">πριν ή μετά τις διακοπές;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μας μήνυμα στο Instagram και θα σχεδιάσουμε μαζί το σωστό timing, ώστε το τατουάζ σου να επουλωθεί σωστά και το καλοκαίρι σου να μην ανατραπεί.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-aftercare-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Φροντίδα Τατουάζ Μετά το Studio</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πλήρεις οδηγίες aftercare για γρήγορη και σωστή επούλωση.</p>
              </Link>
              <Link to="/blog/tattoo-placement-guide-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Οδηγός για Placement Τατουάζ</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πώς να επιλέξεις σωστά το σημείο, και πώς επηρεάζει την επούλωση.</p>
              </Link>
              <Link to="/blog/tatouaz-athina-touristes" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Τατουάζ στην Αθήνα για Τουρίστες</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Ήλιος, θάλασσα και τι να ξέρεις πριν κάνεις τατουάζ ως επισκέπτης.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/color-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Color Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
